import { DateValue } from '@internationalized/date';
import { Spacing } from '@persona-id/design-tokens';
import { DatabaseIcon } from '@persona-id/design-tokens/icons';
import { RangeValue } from '@react-types/shared';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import styled from 'styled-components';

import AnalyticsDatePicker from 'dashboard/components/AnalyticsDatePicker';
import { AnalyticsDateRange } from 'dashboard/components/AnalyticsDatePicker/AnalyticsDatePicker';
import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import ReactMultiSelect from 'dashboard/components/ReactMultiSelect';
import { generatePlaceHolderDisplay } from 'dashboard/components/ReactMultiSelect/ReactMultiSelect';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import {
  MetricsApiLogPathDatum,
  MetricsApiLogPaths,
  MetricsApiLogVolumeOverTime,
} from 'dashboard/models/metrics-api-logs';
import { MetricsApiLogFilterParams } from 'dashboard/redux/slices/metrics-api-logs';
import ApiMetrics from 'dashboard/views/DashboardApiAnalyticsView/ApiMetrics';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card/Card';
import { Label } from 'design-system/components/Text';
import { Iso8601DateString } from 'lib/types';

const SearchAndFilterCardBody = styled(Card.Body)``;

const SearchAndFilterCardTitle = styled(Card.Title)``;

const SearchAndFilterContainer = styled.div`
  display: flex;
  gap: ${Spacing[2]};
  align-items: flex-end;
`;

const StyledInfoIcon = styled(DatabaseIcon)`
  margin-bottom: -${Spacing[1]};
`;

const DatePickerContainer = styled.div`
  min-width: 300px;
`;

const ApiPathsDropdownContainer = styled.div`
  flex-grow: 1;
  max-width: 500px;
`;

const RunContainer = styled.div`
  display: flex;
`;

const RunButton = styled(Button)`
  height: ${Spacing[6]};
`;

export interface DashboardApiAnalyticsViewProps {
  queriedAt: Iso8601DateString | null;
  apiLogFilters: MetricsApiLogFilterParams;
  apiPaths: MetricsApiLogPaths;
  selectedApiPaths: MetricsApiLogPathDatum[];
  volumeOverTime: MetricsApiLogVolumeOverTime;
  loadApiLogMetrics: (apiPaths: MetricsApiLogPathDatum[]) => void;
  handleApiPathFilterUpdate: (apiPaths: string[]) => void;
  handleDateUpdateV2: (range: RangeValue<DateValue> | null) => void;
}

const DashboardApiAnalyticsView = ({
  queriedAt,
  apiLogFilters,
  apiPaths,
  selectedApiPaths,
  volumeOverTime,
  loadApiLogMetrics,
  handleApiPathFilterUpdate,
  handleDateUpdateV2,
}: DashboardApiAnalyticsViewProps) => {
  const { createdAtStart, createdAtEnd } = apiLogFilters;
  const dateRange = useMemo<AnalyticsDateRange>(() => {
    return {
      startDate: createdAtStart ? DateTime.fromISO(createdAtStart, { setZone: true }) : undefined,
      endDate: createdAtEnd ? DateTime.fromISO(createdAtEnd, { setZone: true }) : undefined,
    };
  }, [createdAtStart, createdAtEnd]);

  const apiPathFilterOptions = useMemo(
    () =>
      apiPaths.data.map((apiPath) => ({
        label: `${apiPath.request_method} ${apiPath.request_path} (${apiPath.count})`,
        value: `${apiPath.request_method} ${apiPath.request_path} ${apiPath.count}`,
      })),
    [apiPaths],
  );

  const updatedAt = volumeOverTime.updated_at;
  return (
    <Container>
      <InactiveEnvironmentBanner />
      <Header title='Analytics: API' />
      <Section>
        <Card>
          <SearchAndFilterCardBody>
            <SearchAndFilterCardTitle>Query</SearchAndFilterCardTitle>
            <SearchAndFilterContainer>
              <DatePickerContainer>
                <AnalyticsDatePicker
                  dataTest='metrics-apis-view__date-range'
                  handleDatesChangeV2={handleDateUpdateV2}
                  dateRange={dateRange}
                />
              </DatePickerContainer>
              <ApiPathsDropdownContainer>
                <FormControlLabel>API Paths</FormControlLabel>
                <ReactMultiSelect
                  value={selectedApiPaths.map((apiPath) => {
                    return `${apiPath.request_method} ${apiPath.request_path} ${apiPath.count}`;
                  })}
                  options={apiPathFilterOptions}
                  placeholderGenerator={generatePlaceHolderDisplay({
                    label: { single: 'API Path', multiple: 'API Paths' },
                    emptyIsAll: true,
                  })}
                  onChange={handleApiPathFilterUpdate}
                  data-test='metrics-apis-view__select-template'
                  size='md'
                  selectAllEnabled
                  searchEnabled
                  hideSelectedOptions={false}
                />
              </ApiPathsDropdownContainer>
              <RunContainer>
                {queriedAt != null && (
                  <Label size='md' color='tertiary' weight='bold'>
                    Last ran {DateTime.fromISO(queriedAt).toRelative()}
                  </Label>
                )}
                <RunButton
                  id='metrics-apis-view__run'
                  onClick={() => loadApiLogMetrics(selectedApiPaths)}
                >
                  Run
                </RunButton>
                {updatedAt != null && (
                  <HelpIcon icon={<StyledInfoIcon />}>
                    Data is up to date as of {DateTime.fromISO(updatedAt).toRelative()}
                  </HelpIcon>
                )}
              </RunContainer>
            </SearchAndFilterContainer>
          </SearchAndFilterCardBody>
        </Card>
        <ApiMetrics isLoading={false} volumeOverTime={volumeOverTime} />
      </Section>
    </Container>
  );
};

export default DashboardApiAnalyticsView;
